<template>
	<section>
		<ButtonBack />
		<el-row>
			<el-col :sm="12">
				<h1>Kontakte</h1>
			</el-col>
			<el-col :sm="12" class="flex flex--justifyend">
				<RouterLink :to="'/kontakt-anlegen'" class="el-button el-button--danger is-round" :title="'Neuen Kontakt anlegen'">
					<el-icon><Plus /></el-icon>Neuer Kontakt
				</RouterLink>
			</el-col>
		</el-row>

		<h2>Filter</h2>
		<TheForm :model="request" :rules="rules" :id="id" ref="formRef" :persistForm="true" formNamespace="kontaktFilter" :autoSave="true">
			<el-row>
				<el-form-item label="Freitextsuche" prop="searchTerm" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
					<el-input v-model="request.searchTerm" placeholder="Suche" clearable />
				</el-form-item>

				<el-form-item label="Gruppe" prop="role" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
					<el-select v-model="request.role" placeholder="Alle">
						<el-option v-for="item in contactRoleOptions" :key="item.value" :label="item.label" :value="item" />
					</el-select>
				</el-form-item>
			</el-row>

			<el-row>
				<el-form-item label="Zeitraum (Erstellung)" prop="timePeriod" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
					<el-checkbox-group v-model="selectedTimePeriod" placeholder="Alle">
						<el-checkbox
							v-for="item in timePeriodOptions"
							:key="item.value"
							:value="item.value"
							:border="true"
							@change="() => handleClickOptionWrapper(item)"
						>
							{{ item.label }}
						</el-checkbox>
					</el-checkbox-group>
				</el-form-item>
				<el-col :sm="4" :xs="24"></el-col>
				<el-form-item label="" prop="dateRange" class="el-col el-col-24 el-col-sm-8 el-col-xs-24">
					<div class="datepicker-range__labels">
						<label class="el-form-item__label">Von</label>
						<label class="el-form-item__label">Bis</label>
					</div>
					<el-date-picker
						v-model="dateRange"
						type="daterange"
						:format="'DD.MM.YYYY'"
						range-separator=""
						:clearable="true"
						start-placeholder="TT.MM.JJJJ"
						end-placeholder="TT.MM.JJJJ"
					/>
				</el-form-item>
			</el-row>
			<el-row class="button-panel">
				<TheButton
					type="danger"
					@buttonclick="
						tablePage = 0;
						doSearch();
					"
					isSubmit
					>Anwenden</TheButton
				>
				<TheButton type="default" @buttonclick="resetSearch">
					<el-icon><Close /></el-icon>
					Filter zurücksetzen
				</TheButton>
			</el-row>
		</TheForm>
	</section>

	<div class="bg--white">
		<section id="searchresults">
			<h2>Kontakte ({{ $getCountErgebnisseLabel(usersTotalCount) }})</h2>
			<UsersOrContactsTable
				:loading="usersTableLoading"
				:isUsersTable="false"
				:tableData="filteredUsers"
				:totalCount="usersTotalCount"
				@loadNextPage="loadNextPage"
			/>
		</section>
	</div>
</template>

<script lang="ts">
import UsersOrContactsTable from '@/components/tables/UsersOrContactsTable.vue';
import { searchContacts, searchDecisions, searchUsers } from '@/requests/searches';
import { SearchContactRequest, SearchUserRequest, UserResponse, UsersResponse } from '@/types/types';
import { handleClickTimePeriodOptionCheckbox, handleSearchDateRangeChange, prepareSearchRequest } from '@/utils/search/searchUtils';
import { validateForm, resetForm } from '@/utils/functions';
import { Close, Plus } from '@element-plus/icons-vue';
import type { FormInstance } from 'element-plus';
import { defineComponent, ref } from 'vue';
import ButtonBack from '../components/buttons/ButtonBack.vue';
import TheButton from '../components/TheButton.vue';
import TheForm from '../components/TheForm.vue';
import { returnFormOptions } from '../utils/formOptions.js';
import { texts } from '../utils/texts';

interface SearchContactsData {
	request: SearchContactRequest;
	rules: Object;
	id: string;
	dateRange: string[] | null;
	selectedTimePeriod: any[];
	tablePage: number;
	itemsPerPage: number;
	filteredUsers: UserResponse[];
	usersTotalCount: number;
	usersTableLoading: boolean;
}

export default defineComponent({
	name: 'KontakteView',
	data(): SearchContactsData {
		return {
			request: {
				searchTerm: '',
				role: null,
				fromDate: null,
				toDate: null,
			},
			rules: {},
			id: 'searchForm',
			dateRange: null,
			selectedTimePeriod: [],
			tablePage: 0,
			itemsPerPage: 14,
			filteredUsers: [],
			usersTotalCount: 0,
			usersTableLoading: false,
		};
	},
	setup() {
		const formRef = ref<FormInstance>();
		return {
			formRef,
		};
	},
	components: {
		ButtonBack,
		TheButton,
		TheForm,
		Plus,
		Close,
		UsersOrContactsTable,
	},
	computed: {
		contactRoleOptions() {
			return returnFormOptions('contactRole', false, true);
		},
		timePeriodOptions() {
			return returnFormOptions('timePeriod');
		},
	},
	methods: {
		handleClickOptionWrapper(item) {
			const { newSelectedTimePeriod, newTimePeriodOptions, newDateRange } = handleClickTimePeriodOptionCheckbox(
				item,
				this.selectedTimePeriod,
				this.timePeriodOptions,
			);

			this.selectedTimePeriod = newSelectedTimePeriod;
			this.timePeriodOptions = newTimePeriodOptions;
			this.dateRange = newDateRange;
		},
		resetSearch() {
			resetForm(this.formRef, this.id);
			this.dateRange = null;
			this.tablePage = 0;
			this.doSearch();
		},
		async doSearch() {
			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid) return;

			this.usersTableLoading = true;
			const searchRequest = prepareSearchRequest(this.request);

			const searchResult = await searchContacts(searchRequest, this.tablePage, this.itemsPerPage);
			if (searchResult.success) {
				this.usersTotalCount = searchResult.data.totalCount;
				if (this.tablePage === 0) {
					this.filteredUsers = searchResult.data.items;
				} else {
					this.filteredUsers = [...this.filteredUsers, ...searchResult.data.items];
				}
			} else {
				console.error('searchResult', searchResult.error);
				this.filteredUsers = [];
				this.usersTotalCount = 0;
			}
			this.usersTableLoading = false;
		},
		loadNextPage() {
			this.tablePage++;
			this.doSearch();
		},
	},
	mounted() {
		if (this.$route.query.searchTerm) {
			this.request.searchTerm = String(this.$route.query.searchTerm);
		}

		if (this.$route.query.gruppe) {
			const foundRole = this.contactRoleOptions.find((role) => role.label === this.$route.query.gruppe);
			if (foundRole) {
				this.request.role = foundRole;
			}
		}

		this.doSearch();
	},
	watch: {
		dateRange: {
			handler(val) {
				if (val && val.length === 2) {
					const { fromDate, toDate } = handleSearchDateRangeChange(val);
					this.request.fromDate = fromDate;
					this.request.toDate = toDate;
				} else {
					this.request.fromDate = null;
					this.request.toDate = null;
					this.selectedTimePeriod = [];
					this.timePeriodOptions.forEach((option: any) => (option.checked = false));
				}
			},
			deep: true,
		},
		request: {
			handler() {
				if (this.request.fromDate && this.request.toDate) {
					this.dateRange = [this.request.fromDate, this.request.toDate];
				}
			},
			deep: true,
		},
	},
});
</script>
